import {
    createStyles,
    IconButton,
    List,
    ListItem,
    ListItemText,
    makeStyles,
} from "@material-ui/core";
import {
    AreaNotification,
    baseColors,
    Button,
    CircleIcon,
    Divider,
    EditIcon,
    Label,
    LayoutContainer,
    NoResultsText,
    PlusIcon,
    RelodeTheme,
    Text,
    TrashIcon,
} from "@relode/core";
import { ClientUserRole, JobStatus } from "@relode/core/domain";
import { format } from "date-fns";
import * as React from "react";

import { Team, TeamMember } from "../../../../../shared/types";

const useStyles = makeStyles(
    (theme: RelodeTheme) =>
        createStyles({
            root: {
                display: "flex",
                flexDirection: "column",
            },
            row: {
                display: "flex",
                flexDirection: "row",
                justifyContent: "space-between",
            },
            circle: {
                fontSize: "0.5em",
                color: baseColors.light_blue,
                margin: theme.spacing(0, 1),
            },

            button: {
                marginRight: theme.spacing(2),
                marginLeft: theme.spacing(2),
                [theme.breakpoints.up("md")]: {
                    width: 180,
                },
                marginBottom: 0,
            },
            teamMemberListItemData: {
                marginTop: theme.spacing(1),
                backgroundColor: theme.palette.common.white,
            },
            teamMemberListItemActions: {
                backgroundColor: theme.palette.common.white,
                marginBottom: theme.spacing(1),
                padding: 0,
            },
            listItemContent: {
                paddingLeft: theme.spacing(2),
                paddingRight: theme.spacing(2),
            },
            statusSection: {
                margin: theme.spacing(1, 0),
            },
            teamMemberActions: {
                display: "flex",
                justifyContent: "space-evenly",
                width: "100%",
            },
        }),
    { name: "TeamMembersMobile" }
);

interface TeamMembersMobileProps {
    team: Team;
    handleOpenTeamMembersModal: () => void;
    handleEditTeamMember: (teamMember: TeamMember) => void;
    handleDeleteTeamMember: (teamMember: TeamMember) => void;
    jobStatus: JobStatus;
}
export const TeamMembersMobile = (props: TeamMembersMobileProps) => {
    const classes = useStyles({});
    const {
        team,
        handleEditTeamMember,
        handleDeleteTeamMember,
        handleOpenTeamMembersModal,
        jobStatus,
    } = props;

    const { teamMembers, canManageTeam } = team;

    return (
        <LayoutContainer>
            <AreaNotification areaName="teamPlanWarning" />
            <div className={classes.root}>
                {jobStatus.isPendingOrRequested() ? (
                    <NoResultsText>
                        Not available for jobs in Pending or Requested status.
                    </NoResultsText>
                ) : (
                    <>
                        {canManageTeam ? (
                            <Button
                                id="addTeamMemberButton"
                                size="small"
                                color="default"
                                variant="contained"
                                onClick={handleOpenTeamMembersModal}
                                title="add members"
                                className={classes.button}
                                startIcon={<PlusIcon />}
                            >
                                <span>team member</span>
                            </Button>
                        ) : null}
                        {teamMembers && teamMembers.length > 0 ? (
                            <List id="teamMembersList" component="div">
                                {teamMembers.map((item, i) => (
                                    <TeamMemberItem
                                        key={i}
                                        teamMember={item}
                                        canManageTeam={canManageTeam}
                                        handleEditTeamMember={
                                            handleEditTeamMember
                                        }
                                        handleDeleteTeamMember={
                                            handleDeleteTeamMember
                                        }
                                    />
                                ))}
                            </List>
                        ) : (
                            <NoResultsText>
                                No users assigned to this job.
                            </NoResultsText>
                        )}
                    </>
                )}
            </div>
        </LayoutContainer>
    );
};

const TeamMemberItem = (props: {
    teamMember: TeamMember;
    canManageTeam: boolean;
    handleEditTeamMember: (teamMember: TeamMember) => void;
    handleDeleteTeamMember: (teamMember: TeamMember) => void;
}) => {
    const classes = useStyles({});
    const {
        teamMember,
        canManageTeam,
        handleEditTeamMember,
        handleDeleteTeamMember,
    } = props;

    return (
        <>
            <ListItem
                disableGutters
                alignItems="flex-start"
                className={classes.teamMemberListItemData}
            >
                <ListItemText
                    className={classes.listItemContent}
                    primary={teamMember.fullName}
                    primaryTypographyProps={{
                        variant: "h5",
                        color: "primary",
                    }}
                    secondaryTypographyProps={{ component: "div" }}
                    secondary={
                        <>
                            <Text
                                component="div"
                                className={classes.statusSection}
                            >
                                {
                                    ClientUserRole.fromId(
                                        teamMember.jobUserTypeId
                                    ).displayName
                                }
                                <CircleIcon className={classes.circle} />
                                {teamMember.teamName}
                            </Text>
                            <div className={classes.row}>
                                <Label label="date added">
                                    <Text variant="body2">
                                        {format(
                                            teamMember.createTimestamp,
                                            "P"
                                        )}
                                    </Text>
                                </Label>
                                <Label label="candidates awaiting feedback">
                                    <Text variant="body2">
                                        {
                                            teamMember.candidatesAwaitingFeedbackCount
                                        }
                                    </Text>
                                </Label>
                            </div>
                        </>
                    }
                ></ListItemText>
            </ListItem>
            {!teamMember.isSelf && canManageTeam ? (
                <>
                    <Divider></Divider>
                    <ListItem
                        component="div"
                        disableGutters
                        alignItems="flex-start"
                        className={classes.teamMemberListItemActions}
                    >
                        <div className={classes.teamMemberActions}>
                            <IconButton
                                color="primary"
                                size="medium"
                                onClick={() => handleEditTeamMember(teamMember)}
                            >
                                <EditIcon color="primary" />
                            </IconButton>
                            <IconButton
                                color="primary"
                                size="medium"
                                onClick={() =>
                                    handleDeleteTeamMember(teamMember)
                                }
                            >
                                <TrashIcon color="primary" />
                            </IconButton>
                        </div>
                    </ListItem>
                </>
            ) : null}
        </>
    );
};
